import { useAppDispatch } from "../redux/store";
import { useNavigate, useLocation } from "react-router-dom";
import { openTab, closeTab } from "../redux/actions/workspaceActions";
import {
  SIDEBAR_ITEMS,
  SidebarItem,
  SidebarEntry,
} from "../config/sidebarConfig";
import type { WorkspaceTab } from "../redux/slices/workspace";

const isSidebarItem = (entry: SidebarEntry): entry is SidebarItem => {
  return !("isHeader" in entry);
};

const useWorkspaceNavigation = (): {
  navigateToTab: (path: string) => void;
  closeCurrentAndNavigate: (
    fallbackPath: string,
    tabIdToClose?: string | null,
  ) => void;
} => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const getDynamicTabData = (
    path: string,
  ): Pick<WorkspaceTab, "title" | "componentKey"> | null => {
    if (path === "/purchase-orders/create") {
      return {
        title: "New Purchase Order",
        componentKey: "PURCHASE_ORDER_CREATE",
      };
    }

    const parts = path.split("/");
    if (parts.length === 3) {
      const id = parts[2];
      const shortId = id.substring(0, 8);

      if (path.startsWith("/receipts/"))
        return { title: `Receipt #${shortId}...`, componentKey: "RECEIPT" };
      if (path.startsWith("/invoices/"))
        return { title: `Invoice #${shortId}...`, componentKey: "INVOICE" };
      if (path.startsWith("/proforma-invoices/"))
        return {
          title: `Proforma #${shortId}...`,
          componentKey: "PROFORMA_INVOICE",
        };
      if (path.startsWith("/credit-notes/"))
        return { title: `C.Note #${shortId}...`, componentKey: "CREDIT_NOTE" };
      if (path.startsWith("/debts/"))
        return { title: `Debt #${shortId}...`, componentKey: "DEBT" };
      if (path.startsWith("/purchase-orders/"))
        return {
          title: `P.Order #${shortId}...`,
          componentKey: "PURCHASE_ORDER",
        };
      if (path.startsWith("/quotations/"))
        return { title: `QUOTE #${shortId}...`, componentKey: "QUOTATION" };
    }

    return null;
  };

  const navigateToTab = (path: string): void => {
    const item = SIDEBAR_ITEMS.filter(isSidebarItem).find(
      (i) => i.path === path,
    );
    let tabData: WorkspaceTab | null = null;

    if (item && item.componentKey) {
      tabData = {
        id: item.path,
        title: item.title,
        path: item.path,
        componentKey: item.componentKey,
      };
    } else {
      const dynamicData = getDynamicTabData(path);
      if (dynamicData) {
        tabData = {
          id: path,
          title: dynamicData.title,
          path,
          componentKey: dynamicData.componentKey,
        };
      }
    }

    if (tabData) {
      dispatch(openTab(tabData));
    } else {
      console.warn(`No workspace mapping found for path: ${path}`);
    }

    navigate(path);
  };

  const closeCurrentAndNavigate = (
    fallbackPath: string,
    tabIdToClose: string | null = null,
  ): void => {
    const targetId = tabIdToClose ?? location.pathname;
    dispatch(closeTab(targetId));
    navigateToTab(fallbackPath);
  };

  return { navigateToTab, closeCurrentAndNavigate };
};

export default useWorkspaceNavigation;
